import { useEffect, useState } from "react";
import { toast } from "sonner";
import { Database, Table2, Lock, ChevronLeft, ChevronRight } from "lucide-react";
import { api, apiError } from "@/lib/api";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";

export default function AdminDatabase() {
  const [tables, setTables] = useState([]);
  const [active, setActive] = useState(null);
  const [docs, setDocs] = useState(null);
  const [q, setQ] = useState("");
  const [skip, setSkip] = useState(0);
  const limit = 20;

  useEffect(() => { api.get("/admin/db/tables").then((r) => setTables(r.data.tables)).catch((e) => toast.error(apiError(e.response?.data?.detail))); }, []);

  const loadDocs = (name, s = 0, query = "") => {
    api.get(`/admin/db/tables/${name}`, { params: { q: query || undefined, skip: s, limit } })
      .then((r) => { setDocs(r.data); setActive(name); setSkip(s); }).catch((e) => toast.error(apiError(e.response?.data?.detail)));
  };

  return (
    <div className="grid md:grid-cols-[260px_1fr] gap-4" data-testid="admin-database">
      <div className="bg-card border border-white/[0.06] rounded-xl p-2 max-h-[600px] overflow-auto">
        <div className="text-[10px] uppercase tracking-widest text-zinc-500 px-2 py-1 flex items-center gap-1"><Database className="h-3.5 w-3.5" />Tabelas ({tables.length})</div>
        {tables.map((t) => (
          <button key={t.name} data-testid={`db-table-${t.name}`} disabled={!t.explorable} onClick={() => { setQ(""); loadDocs(t.name, 0, ""); }}
            className={`w-full text-left px-2 py-2 rounded-xl text-sm transition-colors ${active === t.name ? "bg-[#FF4D00]/15 text-[#FF4D00]" : "hover:bg-white/[0.03] text-zinc-300"} ${!t.explorable ? "opacity-40 cursor-not-allowed" : ""}`}>
            <div className="flex items-center justify-between gap-2">
              <span className="flex items-center gap-1.5 truncate"><Table2 className="h-3.5 w-3.5 shrink-0" />{t.name}</span>
              <span className="text-[10px] text-zinc-500 shrink-0">{t.count}{!t.explorable && <Lock className="h-3 w-3 inline ml-1" />}</span>
            </div>
            <div className="text-[10px] text-zinc-600 truncate pl-5">{t.description}</div>
          </button>
        ))}
      </div>

      <div className="bg-card border border-white/[0.06] rounded-xl p-4 min-h-[300px]">
        {!docs ? <div className="text-zinc-500 text-sm h-full flex items-center justify-center">Selecione uma tabela para explorar (somente leitura).</div> : (
          <div className="space-y-3">
            <div className="flex items-center gap-2 flex-wrap">
              <span className="font-semibold mono">{docs.name}</span>
              <span className="text-xs text-zinc-500">{docs.total} registro(s)</span>
              <div className="flex-1" />
              <Input data-testid="db-search" value={q} onChange={(e) => setQ(e.target.value)} onKeyDown={(e) => e.key === "Enter" && loadDocs(docs.name, 0, q)} placeholder="Buscar por _id" className="bg-[#09090B] border-white/10 rounded-xl h-8 w-48 text-xs" />
              <Button size="sm" variant="outline" onClick={() => loadDocs(docs.name, Math.max(0, skip - limit), q)} disabled={skip === 0} className="h-8 rounded-xl bg-transparent border-white/10"><ChevronLeft className="h-4 w-4" /></Button>
              <Button size="sm" variant="outline" onClick={() => loadDocs(docs.name, skip + limit, q)} disabled={skip + limit >= docs.total} className="h-8 rounded-xl bg-transparent border-white/10"><ChevronRight className="h-4 w-4" /></Button>
            </div>
            <p className="text-[11px] text-amber-400/70 flex items-center gap-1"><Lock className="h-3 w-3" />Somente leitura. Dados sensíveis (senhas, chaves Pix, tokens) são mascarados.</p>
            <div className="space-y-2 max-h-[460px] overflow-auto" data-testid="db-docs">
              {docs.docs.length === 0 ? <p className="text-sm text-zinc-500 text-center py-6">Nenhum registro.</p> :
                docs.docs.map((d) => (
                  <pre key={d._id} data-testid={`db-doc-${d._id}`} className="text-[11px] bg-[#09090B] border border-white/[0.05] rounded-xl p-3 overflow-auto text-zinc-300 whitespace-pre-wrap break-all">{JSON.stringify(d, null, 2)}</pre>
                ))}
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
